import React, { useEffect, useRef, useState } from 'react';
import ProjectsAPI from '../../api/projects';

/**
 * Payroll editor (M2-B) — project combobox for the entry modals. Typeahead over
 * `admin/projects/typeahead` (with the api layer's transparent searchAll
 * fallback). Selected value renders as `24-118 · Sunrise Ridge Ph 2`; when
 * `allowNone`, clearing the field selects the bare "No job" option.
 *
 * Keyboard: while the dropdown is open, Enter/Escape/arrows are the PICKER's
 * (stopPropagation) so they never trigger the modal's save/cancel contract.
 */

export interface PickedProject {
  projectID: number;
  altProjectID: string;
  name: string;
}

export interface ProjectPickerProps {
  value: PickedProject | null;
  onChange: (project: PickedProject | null) => void;
  allowNone?: boolean;
  noneLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /** Required-field failure ring (the 2020 hourly/piece-hours gating). */
  bad?: boolean;
}

const projectLabel = (p: PickedProject) => `${p.altProjectID} · ${p.name}`;

const ProjectPicker: React.FC<ProjectPickerProps> = ({
  value, onChange, allowNone = false, noneLabel = 'No job — bare hours',
  disabled = false, placeholder = 'Search projects…', autoFocus = false, bad = false,
}) => {
  const [text, setText] = useState(value ? projectLabel(value) : '');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<PickedProject[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const seqRef = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(value ? projectLabel(value) : '');
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (ev: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runSearch = (q: string) => {
    const seq = ++seqRef.current;
    setSearching(true);
    ProjectsAPI.typeahead(q, 25)
      .then((rows) => {
        if (seq !== seqRef.current) return;
        setOptions(
          (rows ?? []).map((p) => ({
            projectID: p.projectID, altProjectID: p.altProjectID, name: p.name,
          }))
        );
        setHighlight(0);
      })
      .catch(() => {
        if (seq === seqRef.current) setOptions([]);
      })
      .finally(() => {
        if (seq === seqRef.current) setSearching(false);
      });
  };

  const onInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const q = ev.target.value;
    setText(q);
    setOpen(true);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setOptions([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => runSearch(q.trim()), 250);
  };

  const pick = (p: PickedProject | null) => {
    onChange(p);
    setText(p ? projectLabel(p) : '');
    setOpen(false);
  };

  const listItems: Array<PickedProject | null> = allowNone ? [null, ...options] : [...options];

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (!open) {
      if (ev.key === 'ArrowDown') {
        setOpen(true);
        ev.preventDefault();
        ev.stopPropagation();
      }
      return;
    }
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      ev.stopPropagation();
      setHighlight((h) => {
        const n = listItems.length;
        if (n === 0) return 0;
        return ev.key === 'ArrowDown' ? Math.min(h + 1, n - 1) : Math.max(h - 1, 0);
      });
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      ev.stopPropagation();
      if (listItems.length > 0) pick(listItems[Math.min(highlight, listItems.length - 1)]);
      else setOpen(false);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      setOpen(false);
      setText(value ? projectLabel(value) : '');
    }
  };

  return (
    <div className="pe-picker" ref={rootRef}>
      <input
        className={`pe-in sans${bad ? ' bad' : ''}`}
        value={text}
        onChange={onInput}
        onFocus={(ev) => ev.target.select()}
        onKeyDown={onKeyDown}
        placeholder={allowNone ? noneLabel : placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (
        <div className="pe-picker-menu" role="listbox">
          {allowNone && (
            <button
              type="button"
              role="option"
              aria-selected={value == null}
              className={`pe-picker-opt none${highlight === 0 ? ' hl' : ''}`}
              onMouseDown={(ev) => { ev.preventDefault(); pick(null); }}
            >
              {noneLabel}
            </button>
          )}
          {options.map((p, i) => {
            const idx = allowNone ? i + 1 : i;
            return (
              <button
                key={p.projectID}
                type="button"
                role="option"
                aria-selected={value?.projectID === p.projectID}
                className={`pe-picker-opt${highlight === idx ? ' hl' : ''}`}
                onMouseDown={(ev) => { ev.preventDefault(); pick(p); }}
              >
                <span className="mono">{p.altProjectID}</span> {p.name}
              </button>
            );
          })}
          {options.length === 0 && (
            <div className="pe-picker-empty">
              {searching ? 'Searching…' : text.trim().length < 2 ? 'Type to search projects' : 'No matches'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectPicker;
